import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_COUNTRIES, defaultCurrencyForCountry } from '@boletra/shared';

/**
 * Sección "21 países. 15 monedas. Un Stripe." Lista canónica de cobertura
 * en el landing. La fuente de verdad es `SUPPORTED_COUNTRIES` en
 * `@boletra/shared`: si agregás un país ahí, aparece acá automáticamente.
 *
 * Display-only — el cobro real se hace via Stripe Connect Express + Adaptive
 * Pricing, mismo flujo que el dashboard.
 */
export function CoverageSection() {
  const { t, i18n } = useTranslation('landing');
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';

  const items = useMemo(
    () =>
      SUPPORTED_COUNTRIES.map((c) => ({
        code: c.code,
        flag: c.flag,
        name: c.name[lang],
        currency: defaultCurrencyForCountry(c.code),
      })),
    [lang],
  );

  return (
    <section id="coverage" className="border-t border-neutral-900 py-14 sm:py-20 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4">
        <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-2">
          {t('coverage.eyebrow')}
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl">
          {t('coverage.title')}
        </h2>
        <p className="text-neutral-400 mt-3 max-w-3xl">{t('coverage.subtitle')}</p>

        <ul
          className="mt-10 grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          aria-label={t('coverage.title')}
        >
          {items.map((c) => (
            <li
              key={c.code}
              className="flex items-center gap-3 rounded-xl border border-neutral-900 bg-neutral-950/40 px-3 py-2.5 transition hover:border-neutral-700"
            >
              <span className="text-2xl leading-none select-none" aria-hidden>
                {c.flag}
              </span>
              <div className="leading-tight min-w-0">
                <p className="text-sm font-medium text-neutral-100 truncate">{c.name}</p>
                <p className="text-[11px] uppercase tracking-wider text-neutral-500">
                  {c.currency}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-xs text-neutral-500 max-w-2xl">{t('coverage.footer')}</p>
      </div>
    </section>
  );
}
